interface EmbeddedScript {
  readonly fullMatch: string
  readonly interpreter: string
  readonly scriptContent: string
  readonly placeholder: string
  readonly quoteChar: string
  readonly isHeredoc?: boolean
  readonly heredocDelimiter?: string
}

interface CommandSegment {
  readonly content: string
  readonly operator?: string
}

enum FormattingStrategy {
  PRESERVE_NEWLINES = 'PRESERVE_NEWLINES',
  SINGLE_LINE = 'SINGLE_LINE',
  SINGLE_SEGMENT = 'SINGLE_SEGMENT',
  MULTI_LINE = 'MULTI_LINE',
}

export function formatBashCommand(command: string): string {
  const preprocessed = preprocessCommand(command)
  const { command: processedCommand, scripts } = extractEmbeddedScripts(preprocessed)

  const strategy = determineFormattingStrategy(processedCommand, scripts.length > 0)

  return applyFormattingStrategy(strategy, processedCommand, scripts)
}

interface TokenizerState {
  inSingleQuote: boolean
  inDoubleQuote: boolean
  escaped: boolean
}

function tokenizeCommand(command: string): CommandSegment[] {
  const segments: CommandSegment[] = []
  let currentSegment = ''
  let i = 0
  const state: TokenizerState = { inSingleQuote: false, inDoubleQuote: false, escaped: false }

  while (i < command.length) {
    const char = command[i] ?? ''
    const nextChar = command[i + 1] ?? ''
    const result = processCharacter(char, nextChar, state, currentSegment, segments)

    currentSegment = result.currentSegment
    i += result.advance
    state.escaped = result.escaped
    state.inSingleQuote = result.inSingleQuote
    state.inDoubleQuote = result.inDoubleQuote
  }

  if (currentSegment.trim()) {
    segments.push({ content: currentSegment.trim() })
  }

  return segments
}

interface ProcessCharacterResult {
  currentSegment: string
  advance: number
  escaped: boolean
  inSingleQuote: boolean
  inDoubleQuote: boolean
}

function processCharacter(
  char: string,
  nextChar: string,
  state: TokenizerState,
  currentSegment: string,
  segments: CommandSegment[],
): ProcessCharacterResult {
  const { inSingleQuote, inDoubleQuote, escaped } = state

  if (char === '\\' && !escaped) {
    return {
      currentSegment: currentSegment + char,
      advance: 1,
      escaped: true,
      inSingleQuote,
      inDoubleQuote,
    }
  }

  if (char === "'" && !inDoubleQuote && !escaped) {
    return {
      currentSegment: currentSegment + char,
      advance: 1,
      escaped: false,
      inSingleQuote: !inSingleQuote,
      inDoubleQuote,
    }
  }

  if (char === '"' && !inSingleQuote && !escaped) {
    return {
      currentSegment: currentSegment + char,
      advance: 1,
      escaped: false,
      inSingleQuote,
      inDoubleQuote: !inDoubleQuote,
    }
  }

  const operatorResult = tryParseOperator(char, nextChar, state, currentSegment, segments)
  if (operatorResult) return operatorResult

  return {
    currentSegment: currentSegment + char,
    advance: 1,
    escaped: false,
    inSingleQuote,
    inDoubleQuote,
  }
}

function tryParseOperator(
  char: string,
  nextChar: string,
  state: TokenizerState,
  currentSegment: string,
  segments: CommandSegment[],
): ProcessCharacterResult | null {
  const { inSingleQuote, inDoubleQuote, escaped } = state

  if (inSingleQuote || inDoubleQuote || escaped) return null

  const twoChar = char + nextChar
  if (twoChar === '&&' || twoChar === '||') {
    segments.push({ content: currentSegment.trim(), operator: twoChar })
    return { currentSegment: '', advance: 2, escaped: false, inSingleQuote, inDoubleQuote }
  }

  if (char === '|' || char === ';') {
    segments.push({ content: currentSegment.trim(), operator: char })
    return { currentSegment: '', advance: 1, escaped: false, inSingleQuote, inDoubleQuote }
  }

  return null
}

interface HeredocMatch {
  readonly interpreter: string
  readonly delimiter: string
  readonly matchStart: number
  readonly matchEnd: number
}

function extractHeredocScripts(
  command: string,
  scriptIndex: number,
): { command: string; scripts: EmbeddedScript[] } {
  const scripts: EmbeddedScript[] = []
  let processedCommand = command
  let currentIndex = scriptIndex
  const heredocRegex = /(python[23]?|bash|sh|perl|ruby|node|php)\s+<<\s*'?(\w+)'?/g

  let match = heredocRegex.exec(processedCommand)

  while (match !== null) {
    const heredocMatch = parseHeredocMatch(match, heredocRegex.lastIndex)
    if (!heredocMatch) {
      match = heredocRegex.exec(processedCommand)
      continue
    }

    const result = processHeredocMatch(processedCommand, heredocMatch, currentIndex)
    if (result) {
      scripts.push(result.script)
      processedCommand = result.processedCommand
      currentIndex++
      heredocRegex.lastIndex = 0
    }
    match = heredocRegex.exec(processedCommand)
  }

  return { command: processedCommand, scripts }
}

function parseHeredocMatch(match: RegExpExecArray, lastIndex: number): HeredocMatch | null {
  const interpreter = match[1]
  const delimiter = match[2]
  if (!interpreter || !delimiter) return null

  return { interpreter, delimiter, matchStart: match.index, matchEnd: lastIndex }
}

function processHeredocMatch(
  command: string,
  heredocMatch: HeredocMatch,
  currentIndex: number,
): { script: EmbeddedScript; processedCommand: string } | null {
  const { interpreter, delimiter, matchStart, matchEnd } = heredocMatch
  const delimiterPattern = new RegExp(`\n${delimiter}(?:\n|$)`, 'm')
  const endMatch = delimiterPattern.exec(command.slice(matchEnd))

  if (!endMatch) return null

  const scriptEnd = matchEnd + endMatch.index
  const scriptContent = command.slice(matchEnd, scriptEnd)
  const fullMatchEnd = scriptEnd + endMatch[0].length - 1
  const fullMatch = command.slice(matchStart, fullMatchEnd)
  const placeholder = `${PLACEHOLDER_PREFIX}${currentIndex}${PLACEHOLDER_SUFFIX}`

  const script: EmbeddedScript = {
    fullMatch,
    interpreter,
    scriptContent,
    placeholder,
    quoteChar: '',
    isHeredoc: true,
    heredocDelimiter: delimiter,
  }

  return { script, processedCommand: command.replace(fullMatch, placeholder) }
}

function extractEmbeddedScripts(command: string): { command: string; scripts: EmbeddedScript[] } {
  const scripts: EmbeddedScript[] = []
  let processedCommand = command
  let scriptIndex = 0

  const heredocResult = extractHeredocScripts(processedCommand, scriptIndex)
  processedCommand = heredocResult.command
  scripts.push(...heredocResult.scripts)
  scriptIndex += heredocResult.scripts.length

  for (const pattern of EMBEDDED_SCRIPT_PATTERNS) {
    const regex = new RegExp(pattern.regex, 'g')
    let match = regex.exec(processedCommand)

    while (match !== null) {
      const fullMatch = match[0]
      const interpreter = match[1]
      const quotedScript = match[2]

      if (quotedScript && interpreter) {
        const quoteChar = quotedScript[0] ?? ''
        const scriptContent = quotedScript.slice(1, -1)

        const placeholder = `${PLACEHOLDER_PREFIX}${scriptIndex}${PLACEHOLDER_SUFFIX}`
        scriptIndex++

        scripts.push({
          fullMatch,
          interpreter,
          scriptContent,
          placeholder,
          quoteChar,
        })

        processedCommand = processedCommand.replaceAll(fullMatch, placeholder)
      }

      match = regex.exec(processedCommand)
    }
  }

  return { command: processedCommand, scripts }
}

function formatEmbeddedScript(script: EmbeddedScript, baseIndent: string): string {
  const formatted = script.isHeredoc
    ? script.scriptContent
    : script.scriptContent.replaceAll(String.raw`\n`, '\n')

  const lines = formatted.split('\n')

  const indentedLines = lines.map((line, index) => {
    if (index === 0) {
      return line
    }
    return baseIndent + INDENT + line
  })

  return indentedLines.join('\n')
}

function reinjectEmbeddedScripts(formattedCommand: string, scripts: EmbeddedScript[]): string {
  let result = formattedCommand

  for (const script of scripts) {
    const lines = result.split('\n')
    let baseIndent = ''

    for (const line of lines) {
      if (line.includes(script.placeholder)) {
        const regex = /^(\s*)/
        const match = regex.exec(line)
        baseIndent = match?.[1] ?? ''
        break
      }
    }

    const formattedScript = formatEmbeddedScript(script, baseIndent)

    let reconstruction: string
    if (script.isHeredoc) {
      reconstruction = `${script.interpreter} << '${script.heredocDelimiter}'\n${formattedScript}\n${baseIndent}${script.heredocDelimiter}`
    } else {
      reconstruction = `${script.interpreter} -${getScriptFlag(script.interpreter)} ${script.quoteChar}\n${formattedScript}\n${baseIndent}${script.quoteChar}`
    }

    result = result.replaceAll(script.placeholder, reconstruction)
  }

  return result
}

function getScriptFlag(interpreter: string): string {
  for (const [key, flag] of Object.entries(SCRIPT_FLAGS)) {
    if (interpreter.startsWith(key)) {
      return flag
    }
  }
  return 'c'
}

function preprocessCommand(command: string): string {
  return command.replaceAll(String.raw`\n`, '\n')
}

function normalizeWhitespace(command: string): string {
  return command.trim().replaceAll(/\s+/g, ' ')
}

function determineFormattingStrategy(
  command: string,
  hasEmbeddedScripts: boolean,
): FormattingStrategy {
  if (command.includes('\n')) {
    return FormattingStrategy.PRESERVE_NEWLINES
  }

  const normalized = normalizeWhitespace(command)

  if (normalized.length <= MAX_LINE_LENGTH && !hasEmbeddedScripts) {
    return FormattingStrategy.SINGLE_LINE
  }

  const segments = tokenizeCommand(normalized)
  if (segments.length === 1) {
    return FormattingStrategy.SINGLE_SEGMENT
  }

  return FormattingStrategy.MULTI_LINE
}

function applyFormattingStrategy(
  strategy: FormattingStrategy,
  command: string,
  scripts: EmbeddedScript[],
): string {
  switch (strategy) {
    case FormattingStrategy.PRESERVE_NEWLINES:
      return applyPreserveNewlinesStrategy(command, scripts)
    case FormattingStrategy.SINGLE_LINE:
      return applySingleLineStrategy(command)
    case FormattingStrategy.SINGLE_SEGMENT:
      return applySingleSegmentStrategy(command, scripts)
    case FormattingStrategy.MULTI_LINE:
      return applyMultiLineStrategy(command, scripts)
  }
}

function applyPreserveNewlinesStrategy(command: string, scripts: EmbeddedScript[]): string {
  if (scripts.length > 0) {
    return reinjectEmbeddedScripts(command, scripts)
  }
  return command
}

function applySingleLineStrategy(command: string): string {
  return command.trim()
}

function applySingleSegmentStrategy(command: string, scripts: EmbeddedScript[]): string {
  const normalized = normalizeWhitespace(command)
  if (scripts.length > 0) {
    return reinjectEmbeddedScripts(normalized, scripts)
  }
  return normalized
}

interface SegmentProcessingContext {
  readonly segment: CommandSegment
  readonly isFirst: boolean
  readonly isLast: boolean
}

function formatFirstSegment(context: SegmentProcessingContext): {
  line: string
  shouldPush: boolean
} {
  const { segment, isLast } = context
  let line = segment.content

  if (segment.operator && !isLast) {
    line += ' \\'
    return { line, shouldPush: true }
  }

  return { line, shouldPush: false }
}

function formatSubsequentSegment(context: SegmentProcessingContext): string {
  const { segment, isLast } = context
  const segmentText = `${segment.operator ? segment.operator + ' ' : ''}${segment.content}`
  let line = INDENT + segmentText

  if (!isLast && segment.operator) {
    line += ' \\'
  }

  return line
}

function applyMultiLineStrategy(command: string, scripts: EmbeddedScript[]): string {
  const normalized = normalizeWhitespace(command)
  const segments = tokenizeCommand(normalized)

  const lines: string[] = []
  let currentLine = ''

  for (let index = 0; index < segments.length; index++) {
    const segment = segments[index]
    if (!segment) continue

    const context: SegmentProcessingContext = {
      segment,
      isFirst: index === 0,
      isLast: index === segments.length - 1,
    }

    if (context.isFirst) {
      const { line, shouldPush } = formatFirstSegment(context)
      currentLine = shouldPush ? '' : line
      if (shouldPush) lines.push(line)
      continue
    }

    lines.push(formatSubsequentSegment(context))
  }

  if (currentLine.trim()) {
    lines.push(currentLine)
  }

  const formatted = lines.join('\n')
  return scripts.length > 0 ? reinjectEmbeddedScripts(formatted, scripts) : formatted
}

const MAX_LINE_LENGTH = 80
const INDENT = '  '
const PLACEHOLDER_PREFIX = '__EMBEDDED_SCRIPT_'
const PLACEHOLDER_SUFFIX = '__'

const EMBEDDED_SCRIPT_PATTERNS = [
  { lang: 'python', regex: /(python[23]?)\s+-c\s+("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/ },
  { lang: 'php', regex: /(php)\s+-r\s+("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/ },
  { lang: 'node', regex: /(node)\s+-e\s+("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/ },
  { lang: 'perl', regex: /(perl)\s+-e\s+("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/ },
  { lang: 'ruby', regex: /(ruby)\s+-e\s+("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/ },
]

const SCRIPT_FLAGS: Record<string, string> = {
  python: 'c',
  python2: 'c',
  python3: 'c',
  php: 'r',
  node: 'e',
  perl: 'e',
  ruby: 'e',
}
